import * as React from 'react';
import { Permissions } from '../../../models/enums/permissions';
import { Button } from '../../../components/button/Button';
import { ConfirmationDialog } from '../../../components/dialog-types/ConfirmationDialog';
import { ChiTooltip } from '../../../components/utility/ChiTooltip';
import { HasPermission } from '../../../containers/authorization-helpers/HasPermission';
import { AppUtils } from '../../../utils/app-utils';
import { TransportationUtils } from '../../helpers';
import { ContainerRead } from '../../models/containers/container-read';
import { ShipmentStatus, ShipmentStatusMetadata } from '../../models/enums/shipment-status';
import { ShipmentType } from '../../models/enums/shipment-type';
import { ShipmentRead } from '../../models/shipments/shipment-read';

interface IProps {
    route: ShipmentRead;
    requiredPermission: Permissions;
    updateRouteState: any;
}

/**
 * Steps a truck or a container through its statuses: Open, In Production (when it is produced), Ready to Ship,
 * In-Transit and Closed.
 */
export const EditRouteStatus = (props: IProps) => {

    const [showConfimDialog, setShowConfirmDialog]  = React.useState(false);

    const isTruck = props.route.type === ShipmentType.Truck;

    // A route that is produced goes from open to production, and the others from open to ready to ship
    const isProduced = TransportationUtils.requiresProduction(props.route.serviceCenterId);

    const movesToProduction = props.route.status === ShipmentStatus.Open && isProduced;

    // The next step is ready to ship, from in production, or from open when the route is not produced
    const movesToReadyToShip = props.route.status === ShipmentStatus.InProduction
        || (props.route.status === ShipmentStatus.Open && !isProduced);

    const updateRouteStatue = async () => {
        setShowConfirmDialog(false);
        await props.updateRouteState();
    };

    const getButtonLabel = () => {
        if (movesToProduction) {
            return "Move to In Production >";
        }

        if (movesToReadyToShip) {
            return "Move to Ready to Ship >";
        }

        switch (props.route.status) {
            case ShipmentStatus.ReadyToShip:
                return isTruck ? "Move to In-Transit >" : "Move to Shipped >";
            case ShipmentStatus.InTransit:
                return isTruck ? "Close Truck >" : "Close Container >";
            default:
                return "";
        }
    };

    const getDialogText = () => {
        if (movesToProduction) {
            return {
                title: "Move to In Production?",
                body: "Are you sure you want to move this shipment to in production?"
            };
        }

        if (movesToReadyToShip) {
            return {
                title: "Move to Ready to Ship?",
                body: "Are you sure you want to move this shipment to ready to ship?"
            };
        }

        switch (props.route.status) {
            case ShipmentStatus.ReadyToShip:
                return {
                    title: isTruck ? "Move to In-Transit?" : "Move to Shipped?",
                    body: isTruck
                        ? "Are you sure you want to move this truck to in-transit? "
                        : "Are you sure you want to move this container to shipped?"
                };
            case ShipmentStatus.InTransit:
                return {
                    title: isTruck ? "Close Truck?" : "Close Container?",
                    body: isTruck
                        ? "Are you sure you want to close this truck?  This action is reserved for when the truck has returned from its scheduled route."
                        : "Are you sure you want to move this container to closed?"
                };
            default:
                return { title: "", body: "" };
        }
    };

    const getHelpText = () => {
        if (movesToReadyToShip) {
            const unproduced = TransportationUtils.getUnproducedOrders(props.route);
            return unproduced.length > 0
                ? `Before moving to ready to ship, every order must be produced.  Not produced yet: ${ unproduced.map(x => x.id).join(', ') }.`
                : "Before moving a container to ready to ship, it must have a service center, name and carrier set.";
        }

        switch (props.route.status) {
            case ShipmentStatus.ReadyToShip:
                return isTruck
                    ? "Before moving a truck to In-Transit, it must have its departure and return times set.  Additionally, all deliveries must have their shipment time set."
                    : "Before moving a container to Shipped, it must have a dealer, service center, name, number, shipment time and carrier set.";
            case ShipmentStatus.InTransit:
                return isTruck
                    ? "Before closing a truck, all deliveries must be marked as delivered."
                    : "";
            default:
                return "";
        }
    };

    const shouldButtonDisable = () => {
        if (movesToReadyToShip) {
            // every order has to be produced, or not need producing, and a container needs its basics
            if (TransportationUtils.getUnproducedOrders(props.route).length > 0) {
                return true;
            }
            if (!isTruck) {
                const c = props.route as ContainerRead;
                return AppUtils.IsEmpty(c.serviceCenterId) || AppUtils.IsEmpty(c.name) || AppUtils.IsEmpty(c.carrierId);
            }
            return false;
        }

        switch (props.route.status) {
            case ShipmentStatus.ReadyToShip:
                return !isTruck && AppUtils.IsEmpty((props.route as ContainerRead).number);
            case ShipmentStatus.InTransit:
                return isTruck && TransportationUtils.hasOpenDeliveries(props.route);
            default:
                return false;
        }
    };

    // A route goes through each status until it is closed
    const canShowStatusButton = () => {
        return movesToProduction
            || movesToReadyToShip
            || props.route.status === ShipmentStatus.ReadyToShip
            || props.route.status === ShipmentStatus.InTransit;
    };

    return <div className="row delivery-status-control divider-row">
            <div className="col-md-12">
                <h2>Status</h2>
                <p>Currently: <span>{ ShipmentStatusMetadata[props.route.status].display }</span></p>
                {
                    canShowStatusButton()
                        ? <div className="advance-status-button">
                                <HasPermission requiredPermissions={[
                                    props.requiredPermission
                                ]}>
                                    <Button
                                        classes="next-status-button attention"
                                        testID="truck-status-btn"
                                        disable={ shouldButtonDisable() }
                                        onBtnClick={(e: any) => {
                                            e.preventDefault();
                                            setShowConfirmDialog(true);
                                        }}
                                        text={getButtonLabel()}
                                    />
                                    {
                                        shouldButtonDisable()
                                        ? <ChiTooltip
                                            title="Why is this disabled?"
                                            body={ <p>{ getHelpText() }</p> }
                                        />
                                        : null
                                    }
                                </HasPermission>
                        </div>
                        : null
                }
                {
                    showConfimDialog
                    ? <ConfirmationDialog
                        {...getDialogText()}
                        successCallback={ () => updateRouteStatue() }
                        cancelCallback={ () => setShowConfirmDialog(false) } />
                    : null
                }
        </div>
    </div>;

};
